import { FC, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom"; // ✅ Import useLocation
import axios from "axios";
import { KTSVG } from "../../../../../../_metronic/helpers";
import { getAuth } from "../../../../../modules/auth";
import Swal from "sweetalert2";

interface LocationState {
  moduleId?: number;
  championshipId?: string;
  returnTab?: string;
}

export const GerenciarConvocados: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const state = location.state as LocationState;
  const championshipId = state?.championshipId;
  const returnTab = state?.returnTab || "CONVOCACAO";
  const moduleId = state?.moduleId || 1;

  const [evento, setEvento] = useState<any>(null);
  const [selecionados, setSelecionados] = useState<any[]>([]);
  const [candidatos, setCandidatos] = useState<any[]>([]);
  const [categorias, setCategorias] = useState<any[]>([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const handleBack = () => {
    if (championshipId) {
      navigate(`/technical-committee/championships/edit/${championshipId}`, {
        state: { moduleId, initialTab: returnTab },
      });
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    const fetchCategorias = async () => {
      if (!auth?.api_token) return;
      try {
        const res = await axios.get(
          `${apiUrl}/technical-committee/categories`,
          {
            headers: { Authorization: `Bearer ${auth.api_token}` },
          },
        );
        setCategorias(res.data);
      } catch (error) {
        console.error("Erro ao buscar categorias", error);
      }
    };
    fetchCategorias();
  }, [apiUrl, auth?.api_token]);

  const fetchData = useCallback(async () => {
    if (!auth?.api_token || !id) return;
    try {
      const headers = { Authorization: `Bearer ${auth.api_token}` };

      const resEvento = await axios.get(
        `${apiUrl}/technical-committee/convocations/${id}`,
        { headers },
      );
      setEvento(resEvento.data);
      const moduloId = resEvento.data._id_modulo;

      let resCandidatos;

      if (categoryFilter && categoryFilter !== "") {
        resCandidatos = await axios.get(
          `${apiUrl}/technical-committee/candidates/filter`,
          {
            params: {
              moduleId: moduloId,
              categoryId: categoryFilter,
            },
            headers,
          },
        );
      } else {
        resCandidatos = await axios.get(
          `${apiUrl}/technical-committee/convocations/candidates`,
          {
            params: { moduleId: moduloId },
            headers,
          },
        );
      }

      const resSelecionados = await axios.get(
        `${apiUrl}/technical-committee/convocations/${id}/athletes`,
        { headers },
      );

      const listaCandidatos = Array.isArray(resCandidatos.data)
        ? resCandidatos.data
        : [];
      const listaSelecionados = Array.isArray(resSelecionados.data)
        ? resSelecionados.data
        : [];

      setSelecionados(listaSelecionados);

      const idsSelecionados = listaSelecionados.map(
        (s: any) => s._id_atleta_ficha,
      );
      const disponiveis = listaCandidatos.filter(
        (c: any) => !idsSelecionados.includes(c.id),
      );

      setCandidatos(disponiveis);
    } catch (error) {
      console.error(error);
      Swal.fire("Erro", "Falha ao carregar dados.", "error");
    }
  }, [id, apiUrl, auth?.api_token, categoryFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = async (atleta: any) => {
    try {
      await axios.post(
        `${apiUrl}/technical-committee/convocations/athletes`,
        {
          _id_convocacao: id,
          _id_atleta_ficha: atleta.id,
        },
        { headers: { Authorization: `Bearer ${auth?.api_token}` } },
      );

      const novoSelecionado = {
        _id_convocado: Date.now(),
        _id_atleta_ficha: atleta.id,
        nome: atleta.nome,
        sobrenome: atleta.sobrenome,
        posicao: atleta.posicao,
      };
      setSelecionados([...selecionados, novoSelecionado]);
      setCandidatos(candidatos.filter((c) => c.id !== atleta.id));
    } catch (error) {
      Swal.fire("Erro", "Não foi possível adicionar.", "error");
    }
  };

  const handleRemove = async (convocadoId: number) => {
    try {
      await axios.delete(
        `${apiUrl}/technical-committee/convocations/athletes/${convocadoId}`,
        {
          headers: { Authorization: `Bearer ${auth?.api_token}` },
        },
      );
      fetchData();
    } catch (error) {
      Swal.fire("Erro", "Não foi possível remover.", "error");
    }
  };

  const filteredCandidatos = candidatos.filter((c) =>
    (c.nome + " " + (c.sobrenome || ""))
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="d-flex flex-column gap-5">
      <div className="card shadow-sm">
        <div className="card-header">
          <h3 className="card-title d-flex flex-column">
            <span className="fw-bold">
              Evento: {evento?.titulo || "Carregando..."}
            </span>
            <span className="text-muted fs-7 mt-1">
              {evento?.local} -{" "}
              {evento?.data_apresentacao
                ? new Date(evento.data_apresentacao).toLocaleString()
                : ""}
            </span>
          </h3>
          <div className="card-toolbar">
            <button className="btn btn-sm btn-light" onClick={handleBack}>
              Voltar
            </button>
          </div>
        </div>
      </div>

      <div className="row g-5">
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header pt-5">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold text-dark">
                  Disponíveis
                </span>
                <span className="text-muted mt-1 fw-semibold fs-7">
                  {filteredCandidatos.length} atletas encontrados
                </span>
              </h3>

              <div className="card-toolbar gap-2">
                <select
                  className="form-select form-select-sm form-select-solid w-150px"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">Todas Categorias</option>
                  {categorias.map((cat: any) => (
                    <option key={cat._id_categoria} value={cat._id_categoria}>
                      {cat.nome}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  className="form-control form-control-sm form-control-solid w-150px"
                  placeholder="Buscar nome..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="card-body p-0 w-100">
              <div
                className="table-responsive"
                style={{ maxHeight: "600px", overflowY: "auto" }}
              >
                <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 w-100">
                  <tbody className="w-100">
                    {filteredCandidatos.map((item) => (
                      <tr key={item.id} className="px-5">
                        <td className="ps-5">
                          <div className="d-flex align-items-center">
                            <div className="symbol symbol-35px me-3">
                              <div className="symbol-label bg-light-primary text-primary fw-bold">
                                {item.inicial_nome}
                                {item.inicial_sobrenome}
                              </div>
                            </div>
                            <div className="d-flex flex-column">
                              <span className="text-gray-800 fw-bold">
                                {item.nome} {item.sobrenome}
                              </span>
                              <span className="text-muted fs-8">
                                {item.posicao} | {item.categoria}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="text-end pe-5">
                          {item.financeiro === "Atrasado" ? (
                            <button
                              className="btn btn-icon btn-sm btn-light-danger"
                              title="Pendência Financeira"
                              onClick={() =>
                                Swal.fire(
                                  "Aviso",
                                  "Atleta com pendência financeira.",
                                  "warning",
                                )
                              }
                            >
                              <KTSVG
                                path="/media/icons/duotune/general/gen050.svg"
                                className="svg-icon-2"
                              />
                            </button>
                          ) : (
                            <button
                              className="btn btn-icon btn-sm btn-light-success"
                              onClick={() => handleAdd(item)}
                              title="Convocar"
                            >
                              <KTSVG
                                path="/media/icons/duotune/arrows/arr012.svg"
                                className="svg-icon-2"
                              />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredCandidatos.length === 0 && (
                      <tr>
                        <td colSpan={2} className="text-center p-5 text-muted">
                          Nenhum atleta disponível.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card shadow-sm h-100 border border-success border-dashed">
            <div className="card-header pt-5">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold text-success">
                  Lista de Convocados
                </span>
                <span className="text-muted mt-1 fw-semibold fs-7">
                  {selecionados.length} atletas selecionados
                </span>
              </h3>
              <div className="card-toolbar">
                <span className="badge badge-light-success fs-7">
                  Selecionados
                </span>
              </div>
            </div>
            <div className="card-body p-0 w-100">
              <div
                className="table-responsive"
                style={{ maxHeight: "600px", overflowY: "auto" }}
              >
                <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                  <tbody>
                    {selecionados.map((item) => (
                      <tr
                        key={item._id_convocado}
                        className="bg-light-success px-5"
                      >
                        <td className="ps-5">
                          <div className="d-flex align-items-center">
                            <div className="symbol symbol-35px me-3">
                              <div className="symbol-label bg-white text-success fw-bold">
                                {item.nome ? item.nome.charAt(0) : "-"}
                              </div>
                            </div>
                            <div className="d-flex flex-column">
                              <span className="text-gray-800 fw-bold">
                                {item.nome} {item.sobrenome}
                              </span>
                              <span className="text-muted fs-8">
                                {item.posicao}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="text-end pe-5">
                          <button
                            className="btn btn-icon btn-sm btn-light-danger"
                            onClick={() => handleRemove(item._id_convocado)}
                            title="Remover da lista"
                          >
                            <KTSVG
                              path="/media/icons/duotune/arrows/arr061.svg"
                              className="svg-icon-2"
                            />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {selecionados.length === 0 && (
                      <tr>
                        <td colSpan={2} className="text-center p-10 text-muted">
                          A lista está vazia.
                          <br />
                          Selecione atletas ao lado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
